import React, { useState, useRef, useEffect } from 'react'
import { FaUserCircle } from 'react-icons/fa'
import { FiBell, FiSearch, FiX } from 'react-icons/fi'
import { MdOutlineKeyboardDoubleArrowLeft, MdOutlineKeyboardDoubleArrowRight } from 'react-icons/md'
import { FiLogOut, FiUser, FiSettings, FiLink } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { Tooltip } from 'antd'
import { useAuth } from '../contexts/AuthContext'
import useRmReferralLink from '../hooks/useRmReferralLink'
import useAdminNotificationCount from '../hooks/useAdminNotificationCount'

export default function DashboardNavbar({ showMiniSidebar, setShowMiniSidebar }) {
    const { user, roles, logout } = useAuth();
    const { count: notificationCount, canView: canViewNotifications } = useAdminNotificationCount();
    const { hasReferralLink, referralLink, copyReferralLink } = useRmReferralLink();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    const searchRef = useRef(null);
    const navigate = useNavigate();
    // Track if screen is xl and above to determine sidebar open state logic
    const [isXlUp, setIsXlUp] = useState(() => {
        if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
            return window.matchMedia('(min-width: 1280px)').matches;
        }
        return false;
    });

    // Listen for breakpoint changes to keep icon direction correct
    useEffect(() => {
        if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
            const mq = window.matchMedia('(min-width: 1280px)');
            const handler = (e) => setIsXlUp(e.matches);
            if (typeof mq.addEventListener === 'function') {
                mq.addEventListener('change', handler);
            } else if (typeof mq.addListener === 'function') {
                mq.addListener(handler);
            }
            return () => {
                if (typeof mq.removeEventListener === 'function') {
                    mq.removeEventListener('change', handler);
                } else if (typeof mq.removeListener === 'function') {
                    mq.removeListener(handler);
                }
            };
        }
    }, []);

    // Close dropdown and search when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsSearchOpen(false);
                setSearchQuery('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleLogout = () => {
        setIsDropdownOpen(false);
        logout().then(() => navigate('/'));
    };

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleSearchToggle = () => {
        setIsSearchOpen(!isSearchOpen);
        if (!isSearchOpen) {
            // Focus the search input when opening
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        } else {
            // Clear search when closing
            setSearchQuery('');
        }
        // Close dropdown if open
        setIsDropdownOpen(false);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            // Handle search logic here
            console.log('Searching for:', searchQuery);
            // You can navigate to search results page or filter current page
            // navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
        }
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Escape') {
            handleSearchToggle();
        }
    };

    const handleCopyReferralLink = async () => {
        const copied = await copyReferralLink();
        if (copied) setIsDropdownOpen(false);
    };

    const displayName = user?.name ?? 'User';
    const email = user?.email ?? '';
    const role = roles?.[0] ?? 'Admin';
    // Determine if the sidebar is currently open for the active breakpoint
    const isSidebarOpen = isXlUp ? !showMiniSidebar : showMiniSidebar;

    return (
        <div className='h-full w-full bg-surface py-1'>
            <div className='flex h-full w-full items-center gap-1'>

                <div className='w-12 md:w-20 h-full flex items-center justify-center  z-[46]  '>
                    {/* this button will be used to toggle the sidebar */}
                    <button
                        className='group text-xl p-1 rounded-full hover:bg-theme-bg-secondary transition-colors duration-200 active:bg-theme-bg-tertiary bg-theme-bg-secondary border border-theme-border-secondary'
                        onClick={() => setShowMiniSidebar(!showMiniSidebar)}
                    >
                        {isSidebarOpen ?
                            <MdOutlineKeyboardDoubleArrowLeft className='text-theme-text-secondary group-hover:text-theme-text-primary transition-colors h-5 w-5' /> :
                            <MdOutlineKeyboardDoubleArrowRight className='text-theme-text-secondary group-hover:text-theme-text-primary transition-colors h-5 w-5' />
                        }
                    </button>
                </div>

                <div className="flex-1 h-full flex justify-between pr-4">
                    {/* logo */}
                    <div className='h-full w-auto  flex items-center justify-center z-[46] '>
                        <img src={"/svg/altCaseNavLogo.svg"} alt="logo" className='h-[28px] w-auto' />
                    </div>

                    {/* Right side icons */}
                    <div className="flex items-center relative" ref={searchRef}>
                        {/* Search Bar - Expanded State */}
                        {isSearchOpen && (
                            <div className="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center bg-theme-bg-secondary rounded-lg border border-theme-border-secondary shadow-lg z-50 transition-all duration-300 ease-out animate-in slide-in-from-right-4">
                                <form onSubmit={handleSearchSubmit} className="flex items-center">
                                    <div className="flex items-center px-3 py-2">
                                        <FiSearch size={18} className="text-theme-text-secondary mr-2" />
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            placeholder="Search..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            onKeyDown={handleSearchKeyDown}
                                            className="bg-transparent text-theme-text-primary placeholder-theme-text-secondary focus:outline-none focus:ring-0 focus:border-transparent focus:shadow-none border-0 outline-0 w-64 sm:w-80 text-sm"
                                            style={{ outline: 'none', boxShadow: 'none' }}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleSearchToggle}
                                            className="ml-2 p-1 text-theme-text-secondary hover:text-theme-text-primary transition-colors"
                                        >
                                            <FiX size={16} />
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* Default Icons - Hidden when search is open */}
                        <div className={`flex items-center transition-all duration-300 ${isSearchOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                            {/* Search Icon */}
                            {/* <button 
                                onClick={handleSearchToggle}
                                className="xxsm:hidden relative p-2 text-theme-text-secondary hover:text-primary focus:outline-none transition-colors"
                            >
                                <FiSearch size={20} />
                            </button> */}

                            {/* Bell Icon */}
                            {canViewNotifications ? (
                                <button
                                    onClick={() => navigate('/notifications')}
                                    className="xxsm:hidden relative p-2 text-theme-text-secondary hover:text-primary focus:outline-none transition-colors ml-1"
                                    aria-label="Notifications"
                                >
                                    <FiBell size={20} />
                                    {notificationCount > 0 && (
                                        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-white leading-none">
                                            {notificationCount > 99 ? '99+' : notificationCount}
                                        </span>
                                    )}
                                </button>
                            ) : null}
                            
                            {/* User Profile Dropdown */}
                            <div className="ml-4 relative" ref={dropdownRef}>
                                <button
                                    onClick={toggleDropdown}
                                    className="flex items-center focus:outline-none"
                                >
                                    <div className="text-right mr-3 hidden sm:block">
                                        <p className="text-sm font-medium text-theme-text-primary">{displayName}</p>
                                        {/* <p className="text-xs text-theme-text-secondary">{'supplier'}</p> */}
                                    </div>
                                    <FaUserCircle size={32} className="text-primary hover:text-secondary-600 transition-colors" />
                                </button>

                                {/* Dropdown Menu */}
                                {isDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-48 bg-theme-bg-secondary rounded-md shadow-lg border border-theme-border-secondary py-1 z-50">
                                        <div className="px-4 py-2 border-b border-theme-border-secondary">
                                            <p className="text-sm font-medium text-theme-text-primary">{displayName}</p>
                                            <p className="text-xs text-theme-text-secondary">{email}</p>
                                        </div>
                                        
                                        <button 
                                            onClick={() => {
                                                navigate('/profile');
                                                setIsDropdownOpen(false);
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-theme-text-secondary hover:bg-theme-bg-tertiary flex items-center"
                                        >
                                            <FiUser className="mr-3" size={16} />
                                            Profile
                                        </button>
                                        
                                        <button className="w-full text-left px-4 py-2 text-sm text-theme-text-secondary hover:bg-theme-bg-tertiary flex items-center">
                                            <FiSettings className="mr-3" size={16} />
                                            Settings
                                        </button>

                                        {hasReferralLink ? (
                                            <Tooltip title={referralLink} placement="left">
                                                <button
                                                    type="button"
                                                    onClick={handleCopyReferralLink}
                                                    className="w-full text-left px-4 py-2 text-sm text-theme-text-secondary hover:bg-theme-bg-tertiary flex items-center"
                                                >
                                                    <FiLink className="mr-3 shrink-0" size={16} />
                                                    RM Referral Link
                                                </button>
                                            </Tooltip>
                                        ) : null}
                                        
                                        <div className="border-t border-theme-border-secondary my-1"></div>
                                        
                                        <button 
                                            onClick={handleLogout}
                                            className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-900/20 flex items-center"
                                        >
                                            <FiLogOut className="mr-3" size={16} />
                                            Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}